/**
 * PF-4548: derives visible weekdays and slot segments from `ChainConfig.practices[].appointmentTimeslotConfig`.
 * Rules match `physiofit_practice_dashboard_combined` `appointmentTimeslotUtils`; builds the patient timeslot table model.
 */
import type {
  AppointmentTimeslotConfig,
  AppointmentTimeslotDayConfig,
  ChainConfig,
} from '@physiofit-digital/physiofit_types';
import type { TFunction } from 'i18next';
import { deriveTimeWindows, formatWindowRange, normalizeOpeningHour, type TimeWindow } from './appointmentTimeWindows';

export const APPOINTMENT_DAY_ORDER = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;

export const APPOINTMENT_SLOT_KEYS = ['morning', 'afternoon', 'evening'] as const;

export type AppointmentDayKey = (typeof APPOINTMENT_DAY_ORDER)[number];
export type AppointmentSlotKey = (typeof APPOINTMENT_SLOT_KEYS)[number];

type AppointmentSegment = TimeWindow & { key: AppointmentSlotKey };

const FALLBACK_START_HOUR = 8;
const FALLBACK_END_HOUR = 20;

const deriveSegments = (startHour: number, endHour: number): AppointmentSegment[] =>
  deriveTimeWindows(startHour, endHour).map((window, index) => ({ ...window, key: APPOINTMENT_SLOT_KEYS[index] }));

const getFallbackSegments = () => deriveSegments(FALLBACK_START_HOUR, FALLBACK_END_HOUR);

export const getPracticeAppointmentTimeslotConfig = (
  chainConfig?: ChainConfig,
  practiceId?: string,
) => {
  if (!chainConfig?.practices) return undefined;
  if (practiceId && chainConfig.practices[practiceId]?.appointmentTimeslotConfig) {
    return chainConfig.practices[practiceId]?.appointmentTimeslotConfig;
  }

  const firstPracticeConfig = Object.values(chainConfig.practices)[0];
  return firstPracticeConfig?.appointmentTimeslotConfig;
};

export const getDayConfig = (config: AppointmentTimeslotConfig | undefined, dayKey: AppointmentDayKey) =>
  config?.[dayKey];

export const getDaySegments = (
  dayConfig: AppointmentTimeslotDayConfig | undefined,
  options?: { useFallbackWhenDisabled?: boolean },
) => {
  const useFallbackWhenDisabled = options?.useFallbackWhenDisabled ?? true;
  const startHour = normalizeOpeningHour(dayConfig?.startHour);
  const endHour = normalizeOpeningHour(dayConfig?.endHour);
  const enabled = dayConfig?.enabled ?? false;

  if (!enabled && !useFallbackWhenDisabled) return [];

  if (enabled && startHour !== undefined && endHour !== undefined && endHour > startHour) {
    const configuredSegments = deriveSegments(startHour, endHour);
    if (configuredSegments.length > 0) return configuredSegments;
  }

  return useFallbackWhenDisabled ? getFallbackSegments() : [];
};

const LEGACY_WEEKDAYS: AppointmentDayKey[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
];

const DAY_I18N_KEYS: Record<AppointmentDayKey, string> = {
  monday: 'DAY_MONDAY_SHORT',
  tuesday: 'DAY_TUESDAY_SHORT',
  wednesday: 'DAY_WEDNESDAY_SHORT',
  thursday: 'DAY_THURSDAY_SHORT',
  friday: 'DAY_FRIDAY_SHORT',
  saturday: 'DAY_SATURDAY_SHORT',
  sunday: 'DAY_SUNDAY_SHORT',
};

const FALLBACK_SLOT_I18N: Record<AppointmentSlotKey, string> = {
  morning: 'TIME_SLOT_MORNING_HOURS',
  afternoon: 'TIME_SLOT_AFTERNOON_HOURS',
  evening: 'TIME_SLOT_EVENING_HOURS',
};

export type PatientTimeslotTableRow = {
  dayKey: AppointmentDayKey;
  dayLabel: string;
  segments: { key: AppointmentSlotKey; displayText: string }[];
};

export type PatientTimeslotTableModel = {
  rows: PatientTimeslotTableRow[];
  columnKeysInOrder: AppointmentSlotKey[];
  validSlotIds: Set<string>;
};

const collectColumnKeys = (rows: PatientTimeslotTableRow[]): AppointmentSlotKey[] =>
  APPOINTMENT_SLOT_KEYS.filter((key) => rows.some((row) => row.segments.some((s) => s.key === key)));

const buildRowsFromConfig = (
  config: AppointmentTimeslotConfig,
  t: TFunction,
): PatientTimeslotTableRow[] => {
  const enabledDays = APPOINTMENT_DAY_ORDER.filter((d) => config[d]?.enabled);
  const dayKeys: AppointmentDayKey[] =
    enabledDays.length > 0 ? enabledDays : [...LEGACY_WEEKDAYS];

  const rows: PatientTimeslotTableRow[] = [];

  for (const dayKey of dayKeys) {
    const dayConfig = getDayConfig(config, dayKey);
    const useFallback = enabledDays.length === 0;
    const segments = getDaySegments(dayConfig, { useFallbackWhenDisabled: useFallback });
    if (segments.length === 0) continue;

    rows.push({
      dayKey,
      dayLabel: t(DAY_I18N_KEYS[dayKey]),
      segments: segments.map((s) => ({
        key: s.key,
        displayText: formatWindowRange(s.startHour, s.endHour),
      })),
    });
  }

  return rows;
};

const buildLegacyRows = (t: TFunction): PatientTimeslotTableRow[] => {
  const segments = getFallbackSegments();
  return LEGACY_WEEKDAYS.map((dayKey) => ({
    dayKey,
    dayLabel: t(DAY_I18N_KEYS[dayKey]),
    segments: segments.map((s) => ({
      key: s.key,
      displayText: t(FALLBACK_SLOT_I18N[s.key]),
    })),
  }));
};

export const buildPatientTimeslotTableModel = (
  chainConfig: ChainConfig | undefined,
  practiceId: string | undefined,
  t: TFunction,
): PatientTimeslotTableModel => {
  const config = getPracticeAppointmentTimeslotConfig(chainConfig, practiceId);

  let rows: PatientTimeslotTableRow[];
  if (!config) {
    rows = buildLegacyRows(t);
  } else {
    rows = buildRowsFromConfig(config, t);
    if (rows.length === 0) {
      rows = buildLegacyRows(t);
    }
  }

  const columnKeysInOrder = collectColumnKeys(rows);
  const validSlotIds = new Set<string>();
  for (const row of rows) {
    for (const seg of row.segments) {
      validSlotIds.add(`${row.dayKey}_${seg.key}`);
    }
  }

  return { rows, columnKeysInOrder, validSlotIds };
};
